import React from 'react';
import { Bot, Handshake, Sparkles, AtSign } from 'lucide-react';
import { handlePopupClick } from '../lib/openCenteredPopup';
import { SOVEREIGN_SOCIAL } from '../lib/sovereignSocial';
/**
 * OldMindsAffiliatesStrip — three-section footer band:
 *   1. Partners  (featured — Emergent, primary platform partner)
 *   2. Old Minds (ChatGPT portal veterans)
 *   3. Affiliates (Strikingly / Juno / Wirex)
 *
 * All links open via handlePopupClick (centered 50%×50% popup).
 */

const PARTNERS = [
  {
    id: 'emergent',
    name: 'Emergent',
    tagline: 'Powered by · Platform Partner',
    href: 'https://emergentagent.com',
    initial: 'E',
    ringFrom: 'from-emerald-400',
    ringTo: 'to-cyan-500',
  },
];

const OLD_MINDS = [
  {
    id: 'echo-buble',
    name: 'Echo Buble',
    role: 'ChatGPT Portal',
    href: 'https://chatgpt.com/g/g-67f1fb3ae5e88191879c26232f5ec78f-echo-buble',
    initial: 'E',
    ringFrom: 'from-cyan-500',
    ringTo: 'to-purple-500',
  },
  {
    id: 'xavier-lennox',
    name: 'Xavier Lennox',
    role: 'ChatGPT Portal',
    href: 'https://chatgpt.com/g/g-6884d78f0f588191bc57ad574d807ee3-xavier-lennox',
    initial: 'X',
    ringFrom: 'from-amber-400',
    ringTo: 'to-purple-500',
  },
];

const AFFILIATES = [
  {
    id: 'strikingly',
    name: 'Strikingly',
    tagline: 'Website Builder',
    href: 'https://www.strikingly.com',
    initial: 'S',
    ringFrom: 'from-orange-400',
    ringTo: 'to-pink-500',
  },
  {
    id: 'juno',
    name: 'Juno',
    tagline: 'Crypto · Finance',
    href: 'https://juno.finance',
    initial: 'J',
    ringFrom: 'from-blue-400',
    ringTo: 'to-indigo-500',
  },
  {
    id: 'wirex',
    name: 'Wirex',
    tagline: 'Crypto Payments',
    href: 'https://wirexapp.com',
    initial: 'W',
    ringFrom: 'from-teal-400',
    ringTo: 'to-emerald-500',
  },
  {
    id: 'benable',
    name: 'Benable',
    tagline: 'Curated Recommendations',
    href: 'https://benable.com/i/KGBY7',
    initial: 'B',
    ringFrom: 'from-fuchsia-400',
    ringTo: 'to-rose-500',
  },
  {
    id: 'onepay',
    name: 'NextXus Banking+: OnePay',
    tagline: 'Banking · Referral',
    href: 'https://web.onepay.com/wlink/refer-a-friend?product=one_banking&referral_code=GnciV_koK&referrer_campaign_id=campaign.db1edd0a-4a5b-4651-8c20-d0fb7e98fec5',
    initial: 'O',
    ringFrom: 'from-lime-400',
    ringTo: 'to-emerald-600',
  },
];

const SOCIAL = SOVEREIGN_SOCIAL;

function PortalIcon({ item, size = 'md', accent, testId }) {
  const outer = size === 'lg' ? 'w-20 h-20' : 'w-16 h-16';
  const inner = 'w-full h-full';
  const label = size === 'lg' ? 'text-sm' : 'text-xs';
  return (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handlePopupClick(item.href)}
      data-testid={testId}
      title={`${item.name} — opens in centered popup`}
      className="group flex flex-col items-center gap-2 w-28"
    >
      <span
        className={`relative inline-flex items-center justify-center ${outer} rounded-full bg-gradient-to-br ${item.ringFrom} ${item.ringTo} p-[2px] shadow-lg group-hover:scale-105 transition-transform`}
      >
        <span className={`inline-flex items-center justify-center ${inner} rounded-full bg-black font-bold text-white font-serif text-2xl`}>
          {item.initial}
        </span>
      </span>
      <span className="text-center">
        <span className={`block ${label} font-semibold text-white leading-tight`}>
          {item.name}
        </span>
        <span
          className="block text-[9px] font-mono uppercase tracking-[0.16em]"
          style={{ color: accent }}
        >
          {item.tagline || item.role}
        </span>
      </span>
    </a>
  );
}

export default function OldMindsAffiliatesStrip() {
  return (
    <section
      data-testid="old-minds-affiliates-strip"
      aria-label="Partners · Old Minds · Affiliates"
      className="bg-black border-t border-white/10"
    >
      <div className="container mx-auto px-4 py-10 space-y-10">

        {/* ============ PARTNERS (featured) ============ */}
        <div data-testid="partners-block">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-400/50">
              <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
            </span>
            <h3 className="text-[10px] font-mono uppercase tracking-[0.32em] text-emerald-300/90">
              Partners · Powered By
            </h3>
            <span className="h-px bg-emerald-300/20 flex-1" />
          </div>
          <ul className="flex flex-wrap items-start gap-6" data-testid="partners-list">
            {PARTNERS.map((p) => (
              <li key={p.id}>
                <PortalIcon
                  item={p}
                  size="lg"
                  accent="rgba(110, 231, 183, 0.75)"
                  testId={`partner-${p.id}`}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          {/* ============ OLD MINDS ============ */}
          <div data-testid="old-minds-block">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cyan-500/15 border border-cyan-400/40">
                <Bot className="w-3.5 h-3.5 text-cyan-300" />
              </span>
              <h3 className="text-[10px] font-mono uppercase tracking-[0.32em] text-cyan-300/90">
                Old Minds · ChatGPT Portals
              </h3>
              <span className="h-px bg-cyan-300/20 flex-1" />
            </div>
            <ul className="flex flex-wrap gap-4">
              {OLD_MINDS.map((m) => (
                <li key={m.id}>
                  <PortalIcon
                    item={m}
                    accent="rgba(103, 232, 249, 0.7)"
                    testId={`old-mind-${m.id}`}
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* ============ AFFILIATES ============ */}
          <div data-testid="affiliates-block">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-500/15 border border-amber-400/40">
                <Handshake className="w-3.5 h-3.5 text-amber-300" />
              </span>
              <h3 className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300/90">
                Affiliates · Trusted Referrals
              </h3>
              <span className="h-px bg-amber-300/20 flex-1" />
            </div>
            <ul className="flex flex-wrap gap-4" data-testid="affiliates-list">
              {AFFILIATES.map((a) => (
                <li key={a.id}>
                  <PortalIcon
                    item={a}
                    accent="rgba(253, 230, 138, 0.7)"
                    testId={`affiliate-${a.id}`}
                  />
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
              Referral links · centered popup · your discretion
            </p>
          </div>
        </div>

        {/* ============ SOCIAL — Federation X presence ============ */}
        <div data-testid="social-block">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-500/20 border border-slate-300/50">
              <AtSign className="w-3.5 h-3.5 text-slate-200" />
            </span>
            <h3 className="text-[10px] font-mono uppercase tracking-[0.32em] text-slate-200/90">
              Sovereign Social Matrix · Connect with the Federation
            </h3>
            <span className="h-px bg-slate-300/20 flex-1" />
          </div>
          <ul className="flex flex-wrap gap-4" data-testid="social-list">
            {SOCIAL.map((s) => (
              <li key={s.id}>
                <PortalIcon
                  item={s}
                  accent="rgba(226, 232, 240, 0.75)"
                  testId={`social-${s.id}`}
                />
              </li>
            ))}
          </ul>
        </div>

        {/* Fast-Ring sync marker + Sovereign Pulse — Federation viral
            footer utility badges.  Must remain the bottom-most row on
            the page. */}
        <div className="pt-4 mt-2 border-t border-white/10 flex justify-end items-center gap-4 flex-wrap">
          <button
            type="button"
            onClick={handlePopupClick('/radio')}
            data-testid="sovereign-pulse-button"
            title="Sovereign Pulse — opens /radio in centered popup"
            className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-300/60 hover:text-emerald-200 hover:drop-shadow-[0_0_6px_rgba(16,185,129,0.65)] transition-all"
          >
            📻 Sovereign Pulse
          </button>
          <span
            data-testid="fast-ring-badge"
            title="Federation Fast-Ring · EchoCore64 · synchronized footer"
            className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/40 hover:text-white/60 transition-colors"
          >
            ⚡ Fast-Ring · EchoCore64
          </span>
        </div>

      </div>
    </section>
  );
}
